import React from 'react';
import type { ContainmentStatus as ContainmentStatusType, MetabolicSnapshot } from '../types';
import { formatPercent, getPercentColor, getPercentBgColor } from '../utils/format';

export interface ContainmentStatusProps {
  status: ContainmentStatusType;
  healthHistory?: MetabolicSnapshot[];
  className?: string;
}

/**
 * BSL-4 Containment Status Panel
 * Shows Active VMs, Network Escapes, CPU/RAM health
 */
export function ContainmentStatus({
  status,
  healthHistory = [],
  className = '',
}: ContainmentStatusProps) {
  const isolationColor = status.isolationScore === 100 ? 'text-green-400' : 'text-yellow-400';

  return (
    <div className={`bg-gray-900 border border-gray-800 rounded-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-white">Containment Status</h2>
        <span className="px-3 py-1 text-xs font-mono bg-purple-900/50 text-purple-300 rounded-full border border-purple-700">
          BSL-4
        </span>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Active VMs */}
        <div>
          <div className="text-sm text-gray-400 mb-1">Active VMs</div>
          <div className="text-2xl font-bold text-white">
            {status.activeVms}
            <span className="text-gray-500 text-lg">/{status.totalVms}</span>
          </div>
        </div>

        {/* Network Escapes */}
        <div>
          <div className="text-sm text-gray-400 mb-1">Network Escapes</div>
          <div className={`text-2xl font-bold ${status.networkEscapes === 0 ? 'text-green-400' : 'text-red-400'}`}>
            {status.networkEscapes}
          </div>
        </div>
      </div>

      {/* Progress Bars */}
      <div className="space-y-4 mb-6">
        {/* CPU */}
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-400">CPU</span>
            <span className={getPercentColor(status.cpuUsage)}>
              {formatPercent(status.cpuUsage)}
            </span>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              className={`h-full ${getPercentBgColor(status.cpuUsage)} transition-all duration-500`}
              style={{ width: `${status.cpuUsage}%` }}
            />
          </div>
        </div>

        {/* Isolation */}
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-400">Isolation</span>
            <span className={isolationColor}>
              {formatPercent(status.isolationScore)}
            </span>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all duration-500"
              style={{ width: `${status.isolationScore}%` }}
            />
          </div>
        </div>
      </div>

      {/* Health Chart */}
      {healthHistory.length > 0 && (
        <div className="border-t border-gray-800 pt-4">
          <div className="text-sm text-gray-400 mb-2">Agent Fleet Health (24h)</div>
          <div className="flex items-end h-12 gap-0.5">
            {healthHistory.slice(-48).map((snapshot, i) => (
              <div
                key={i}
                className={`flex-1 ${snapshot.withinLimits ? 'bg-green-500/60' : 'bg-red-500/60'} rounded-t`}
                style={{ height: `${Math.max(10, snapshot.cpu)}%` }}
                title={`${formatPercent(snapshot.cpu)} CPU at ${snapshot.timestamp}`}
              />
            ))}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            All agents within MetabolicGuardrail
          </div>
        </div>
      )}
    </div>
  );
}
